import React, { useEffect, useState, useRef } from 'react';
import { getTranslation } from '../../../../utils/functions';
import InputDropdown from '../../../../components/Inputs/InputDropdown/InputDropdown';
import { Field, useFormikContext } from 'formik';
import { IDisabilitiesCategoriesApiResponse } from '../../../../services/app-user/IAppUser';
import { ISelectOptions } from '../../../../utils/interfaces/ISelectOptions';
import { FileUploader, Link, Typography } from '@hid-galaxy-ui/galaxy-react';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import {
  getDisabilitiesCategoriesApi,
  uploadFile,
} from '../../../../services/app-user/app-user';
import { NotificationError } from '../../../../utils/notification';
import { IAppProps } from '../../../../App';
import { FILE_UPLOAD_MAX_SIZE_MB } from '../../../../components/Inputs/InputFileUploadSingle/file-upload-constants';
import { useTranslation } from 'react-i18next';
import { getFileNameFromUrl } from '../../../../utils/fileUtils';
import './DisabilityCategories.scss';
import allActions from '../../../../store/actions';
import { useDispatch } from 'react-redux';
import { getDocumentLabelsAndTemplateUrls } from './DisabilityCategoriesHelper';

interface IFileData {
  fileDetails: any;
  error?: { message: string };
}

interface IDisabilityCategoriesFieldsProps extends IAppProps {
  accountData?: any;
  // setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  // onSelectDisabilityCategory: (selectedValue: ISelectOptions) => void;
}

const FEDERAL_CARD_CATEGORY_CODE = 'FEDERAL_CARD';

const DisabilityCategoriesFields = (
  props: IDisabilityCategoriesFieldsProps,
) => {
  const {
    toasts,
    setToasts,
    accountData,
    // setFieldValue,
    // onSelectDisabilityCategory,
  } = props;
  const { setFieldValue, values } = useFormikContext<any>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [disabilitiesCategories, setDisabilitiesCategories] = useState<
    IDisabilitiesCategoriesApiResponse[]
  >([]);
  const [
    uploadDisabilitiesCategoryRequiredDocumentsCount,
    setUploadDisabilitiesCategoryRequiredDocumentsCount,
  ] = useState<number>(0);

  const [
    uploadDisabilitiesCategoryDocumentFiles,
    setUploadDisabilitiesCategoryDocumentFiles,
  ] = useState<string[]>(accountData?.documentUrls ?? []);
  const [documentLabels, setDocumentLabels] = useState<string[]>([]);

  const [documentTemplateUrls, setDocumentTemplateUrls] = useState<string[]>(
    [],
  );
  const previousDisabilityCategoryCode = useRef<string | undefined>(
    accountData?.disabilityCategoryCode,
  );
  const extensiontype = ['pdf', 'jpg', 'jpeg', 'png'];

  useEffect(() => {
    getDisabilitiesCategories();
  }, []);

  const getRequiredDocumentsCount = (
    disabilitiesCategoriesData: IDisabilitiesCategoriesApiResponse[],
    categoryCode: string,
  ) => {
    const disabilityCategory = disabilitiesCategoriesData.find(
      (category: IDisabilitiesCategoriesApiResponse) =>
        category.categoryCode === categoryCode,
    );
    return disabilityCategory?.requiredDocumentsCount;
  };

  const getDisabilitiesCategories = async () => {
    try {
      const response = await getDisabilitiesCategoriesApi();
      setDisabilitiesCategories(response);
      const requiredDocumentsCount = accountData
        ? getRequiredDocumentsCount(
            response,
            accountData?.disabilityCategoryCode,
          )
        : 0;
      setRequiredDocumentsCount(requiredDocumentsCount, false);

      if (accountData?.disabilityCategoryCode) {
        const selectedCategory = response.find(
          (category: IDisabilitiesCategoriesApiResponse) =>
            category.categoryCode === accountData.disabilityCategoryCode,
        );
        if (selectedCategory) {
          const documentLabelsAndTemplateUrls =
            getDocumentLabelsAndTemplateUrls(selectedCategory.description);
          setDocumentLabels(
            documentLabelsAndTemplateUrls.map((item) => item.label),
          );
          setDocumentTemplateUrls(
            documentLabelsAndTemplateUrls.map((item) => item.templateUrl),
          );
        }
      }
    } catch (error) {
      setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
    }
  };
  const getDisabilitiesCategoriesOptions = () => {
    return disabilitiesCategories.map(
      (category: IDisabilitiesCategoriesApiResponse) => ({
        label: category.categoryName,
        value: category.categoryCode,
      }),
    );
  };

  const setRequiredDocumentsCount = (
    requiredDocumentsCount: number,
    shouldClearDocuments: boolean = false,
  ) => {
    setUploadDisabilitiesCategoryRequiredDocumentsCount(requiredDocumentsCount);
    setFieldValue('requiredDocumentsCount', requiredDocumentsCount);
    if (requiredDocumentsCount > 0) {
      if (shouldClearDocuments) {
        const newUrls = Array(requiredDocumentsCount).fill('');
        setDocumentUrls(newUrls);
      } else {
        const currentUrls = uploadDisabilitiesCategoryDocumentFiles;
        const newUrls = Array(requiredDocumentsCount).fill('');
        currentUrls.forEach((url, index) => {
          if (index < requiredDocumentsCount && url) {
            newUrls[index] = url;
          }
        });
        setDocumentUrls(newUrls);
      }
    } else {
      setDocumentUrls([]);
    }
  };

  useEffect(() => {
    const disabilityCategoryCode = values?.disabilityCategoryCode;

    if (
      previousDisabilityCategoryCode.current === undefined &&
      disabilityCategoryCode
    ) {
      previousDisabilityCategoryCode.current = disabilityCategoryCode;
      return;
    }

    if (disabilityCategoryCode) {
      const disabilityCategory = disabilitiesCategories.find(
        (category: IDisabilitiesCategoriesApiResponse) =>
          category.categoryCode === disabilityCategoryCode,
      );
      if (disabilityCategory) {
        // Only clear documents if the category actually changed
        const shouldClearDocuments =
          previousDisabilityCategoryCode.current !== disabilityCategoryCode;
        setRequiredDocumentsCount(
          disabilityCategory.requiredDocumentsCount,
          shouldClearDocuments,
        );
        const documentLabelsAndTemplateUrls = getDocumentLabelsAndTemplateUrls(
          disabilityCategory.description,
        );
        setDocumentLabels(
          documentLabelsAndTemplateUrls.map((item) => item.label),
        );
        setDocumentTemplateUrls(
          documentLabelsAndTemplateUrls.map((item) => item.templateUrl),
        );
      }
    } else {
      // Only clear documents if the field was previously populated
      if (previousDisabilityCategoryCode.current) {
        setUploadDisabilitiesCategoryRequiredDocumentsCount(0);
        setDocumentUrls([]);
        setDocumentLabels([]);
        setDocumentTemplateUrls([]);
      }
    }

    // Update the previous value for next comparison
    previousDisabilityCategoryCode.current = disabilityCategoryCode;
  }, [values?.disabilityCategoryCode, disabilitiesCategories]);

  const renderDisabilityCategoryField = () => {
    return (
      <Field
        key="disabilityCategoryCode"
        component={InputDropdown}
        label={getTranslation('TYPE')}
        name="disabilityCategoryCode"
        id="disabilityCategoryCode"
        isClearable={false}
        value={accountData?.disabilityCategoryCode}
        options={getDisabilitiesCategoriesOptions()}
        required={true}
      />
    );
  };

  const setAcceptedFiles = (types) => {
    return types.map((typ) => `.${typ}`).join(', ');
  };

  const uploadDisabilityCategoryDocument = async (data: IFileData[]) => {
    return await Promise.all(
      data.map(async (fileObj) => {
        const { fileDetails } = fileObj;
        try {
          const response = await uploadFile(fileDetails);
          return response?.location;
        } catch (error) {
          setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
        }
      }),
    );
  };

  const handleFileUpload = async (e, data, documentIndex: number) => {
    dispatch(allActions.appConfigActions.setLoader(true));

    const filteredNewFiles = data.filter(
      (file) => file.fileDetails instanceof File,
    );
    if (filteredNewFiles.length > 0) {
      const fileUploadResponse =
        await uploadDisabilityCategoryDocument(filteredNewFiles);
      dispatch(allActions.appConfigActions.setLoader(false));

      if (!fileUploadResponse) {
        return;
      }

      const updatedDocumentUrls = [...uploadDisabilitiesCategoryDocumentFiles];
      updatedDocumentUrls[documentIndex] = fileUploadResponse[0];
      setDocumentUrls(updatedDocumentUrls);
    }
  };

  const setDocumentUrls = (dataToSet: string[]) => {
    setFieldValue('documentUrls', dataToSet);
    setUploadDisabilitiesCategoryDocumentFiles(dataToSet);
  };

  const handleFileDelete = (
    e: React.MouseEvent<HTMLElement>,
    data: { fileDetails: { name: string } },
    deletedFile: { name: string },
    documentIndex: number,
  ) => {
    const updatedDocumentUrls = [...uploadDisabilitiesCategoryDocumentFiles];
    updatedDocumentUrls[documentIndex] = '';
    setDocumentUrls(updatedDocumentUrls);
  };

  const renderSupportingDocumentTitle = () => {
    return (
      <Typography
        className="hid-spacing__mb-02"
        variant={TypographyVariantEnum.BodyLongMarketing}
      >
        <label className="hid-form-element__label">{`${t('UPLOAD_SUPPORTING_DOCUMENT')}`}</label>
      </Typography>
    );
  };

  const renderDisabilityCategoryDocumentUpload = () => {
    const documentLabelsAndTemplateUrls = getDocumentLabelsAndTemplateUrls(
      disabilitiesCategories[0].description,
    );

    return (
      <div className="hid-spacing__mb-06">
        {Array.from(
          { length: uploadDisabilitiesCategoryRequiredDocumentsCount },
          (_, index) => (
            <div key={`document-${index}`} className="hid-spacing__mb-04">
              {uploadDisabilitiesCategoryRequiredDocumentsCount > 0 && (
                <div className="hid-flex hid-spacing__mb-02">
                  <Typography
                    className="hid-spacing__mb-02 hid-spacing__mr-02"
                    variant={TypographyVariantEnum.BodyLongMarketing}
                  >
                    <label className="hid-form-element__label">
                      {documentLabels[index] || `${t('DOCUMENT')} ${index + 1}`}
                    </label>
                  </Typography>
                  {documentTemplateUrls[index] && (
                    <Link
                      className="hid-spacing__mt-02 template-link"
                      link={`${documentTemplateUrls[index]}`}
                    >
                      {`(${getTranslation('DOWNLOAD_TEMPLATE')})`}
                    </Link>
                  )}
                </div>
              )}
              <Field
                key={`documentUrls-${index}`}
                name={`documentUrls-${index}`}
                component={FileUploader}
                buttonConfig={{ label: t('UPLOAD_DOCUMENT') }}
                data={renderIndividualDocumentData(index)}
                isMultiple={false}
                onUpload={(e, data) => handleFileUpload(e, data, index)}
                onRemove={(e, data, deletedFile) =>
                  handleFileDelete(e, data, deletedFile, index)
                }
                validateConfig={[
                  {
                    maxFileSize: {
                      maxSizeInMb: FILE_UPLOAD_MAX_SIZE_MB,
                      message: t('FILE_UPLOAD_ERROR_FILE_SIZE'),
                    },
                  },
                ]}
              />
            </div>
          ),
        )}
        <p className="hid-spacing__mt-02">
          {`${t('ALLOWED_FILE_TYPE')} ${setAcceptedFiles(extensiontype)}`}
        </p>
        {values?.disabilityCategoryCode === FEDERAL_CARD_CATEGORY_CODE && (
          <Typography
            className="hid-spacing__mt-04"
            variant={TypographyVariantEnum.BodyShortProduct}
          >
            {getTranslation('DOCUMENT_UPLOAD_INSTRUCTION')}
          </Typography>
        )}
      </div>
    );
  };

  const renderIndividualDocumentData = (documentIndex: number) => {
    const url = uploadDisabilitiesCategoryDocumentFiles[documentIndex];
    if (url) {
      return [
        {
          fileDetails: {
            name: getFileNameFromUrl(url),
          },
        },
      ];
    }
    return [];
  };

  return (
    <>
      <div className="hid-spacing__mb-04">
        {renderDisabilityCategoryField()}
      </div>

      {uploadDisabilitiesCategoryRequiredDocumentsCount > 0 && (
        <div className="hid-spacing__mb-04">
          {renderSupportingDocumentTitle()}
        </div>
      )}

      {uploadDisabilitiesCategoryRequiredDocumentsCount > 0 && (
        <div className="hid-spacing__pt-04 hid-spacing__px-06 hid-spacing__mb-03 disability-categories-container">
          {renderDisabilityCategoryDocumentUpload()}
        </div>
      )}
    </>
  );
};

export default DisabilityCategoriesFields;
